import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  Algoliasearch,
  algoliasearch,
  Hit,
  SearchResponse,
} from "algoliasearch";
import debounce from "lodash/debounce";
import { v4 as uuidv4 } from "uuid";

import {
  COLLECTION_ID,
  Space,
  Track,
} from "@highnote/server/src/core/entities";
import { highnote } from "@highnote/server/src/sdk";
import { ToastMessageContent, useToast } from "App/common/useToast";
import { useAuth } from "../Auth";

const ALOGOLIA_INDEX_NAME =
  process.env.DEPLOY_ENV === "production" ? "all_prod_data" : "all_dev_data";

// Define proper types for the context
type AlgoliaToken = {
  publicKey: string;
  validUntil: number;
} | null;

export type SearchResult = (Partial<Space> | Partial<Track>) & {
  path?: string;
};

export type SearchResultsType = {
  spaces: SearchResult[];
  tracks: SearchResult[];
};

interface SearchContextValue {
  searchValue: string;
  searchError: string | null;
  searchResults: SearchResultsType | null;
  isSearching: boolean;
  isSearchClientLoading: boolean;
  isSearchClientLoaded: boolean;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

// Create context with proper default values
export const SearchContext = createContext<SearchContextValue>(null);

/**
 * Algolia token scope for the search context.
 * - "private" → only entities the user has direct read access to (readableByV2).
 * - "public"  → also includes entities with visibility=visible.
 *
 * Note: we technically don't need two tokens. For both Home and Library
 * search, the per-tab/per-surface filtering is handled client-side, and in
 * either case the server only ever returns entities the user is allowed to
 * see. So a single broader token + client-side filtering would be simpler.
 *
 * The split exists because some surfaces (e.g. Move To) want a UX that only
 * surfaces the user's own Library, even though it's technically valid to
 * move an entity into a public/visible space that allows public uploads.
 * Keeping the narrower "private" token avoids leaking those public results
 * into surfaces that don't want to show them, without the surface having to
 * filter them out itself.
 */
export const SearchContextProvider = ({
  children,
  tokenGetter = "private",
}: {
  children: React.ReactNode;
  tokenGetter?: "private" | "public";
}) => {
  const { addMessage } = useToast();
  const { user } = useAuth();

  const [generatedToken, setGeneratedToken] = useState<AlgoliaToken>(null);
  const [client, setClient] = useState<Algoliasearch | null>(null);

  const [searchValue, setSearchValue] = useState("");
  const [isSearchClientLoading, setIsSearchClientLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | undefined>("");
  const [searchResults, setSearchResults] = useState<SearchResultsType | null>(
    null,
  );

  const hasValidSearchToken = () => {
    if (!generatedToken) return false;
    // *1000 to convert to milliseconds
    const timeUntilExpiration = generatedToken.validUntil * 1000 - Date.now();
    // Check if the token is valid for at least 5 minutes
    return timeUntilExpiration > 5 * 60 * 1000; // 5 minutes
  };

  const fetchAlgoliaToken = useCallback(async () => {
    if (!user) return;

    setIsSearchClientLoading(true);
    setSearchError("");

    try {
      const token =
        tokenGetter === "public"
          ? await highnote.getAlgoliaPublicSearchAuthToken()
          : await highnote.getAlgoliaAuthToken();
      setGeneratedToken(token);

      // Get and set the Algolia client after the token is generated
      const client = algoliasearch(
        process.env.REACT_APP_ALGOLIA_APPLICATION_ID,
        token.publicKey,
      );

      if (client) {
        setClient(client);
      }
    } catch (err) {
      setSearchError(
        err instanceof Error ? err.message : "Failed to generate Algolia token",
      );
      setGeneratedToken(null);
    } finally {
      setIsSearchClientLoading(false);
    }
  }, [user, tokenGetter]);

  // Fetch token on mount and when user changes
  useEffect(() => {
    if (!generatedToken) {
      fetchAlgoliaToken();
    }
  }, [generatedToken]);

  const debouncedSearch = useCallback(
    debounce(async (searchValue: string) => {
      if (!client) return;

      if (!searchValue.trim()) {
        setSearchResults(null);
        setIsSearching(false);
        return;
      }

      try {
        if (!hasValidSearchToken()) {
          await fetchAlgoliaToken();
        }

        setIsSearching(true);
        const response = await client.search<SearchResult>({
          requests: [
            {
              indexName: ALOGOLIA_INDEX_NAME,
              query: searchValue,
            },
          ],
        });

        const searchHits = (response.results[0] as SearchResponse<SearchResult>)
          .hits;

        // Organize the results by entity type
        const resultsByEntityType = searchHits.reduce(
          (acc, searchHit) => {
            const entityType = searchHit.path?.includes(COLLECTION_ID.SPACE)
              ? COLLECTION_ID.SPACE
              : searchHit.path?.includes(COLLECTION_ID.TRACK)
                ? COLLECTION_ID.TRACK
                : null;

            if (!entityType) {
              return acc;
            }

            if (entityType === COLLECTION_ID.SPACE) {
              acc.spaces.push(searchHit);
            } else {
              acc.tracks.push(searchHit);
            }

            return acc;
          },
          {
            spaces: [],
            tracks: [],
          } as {
            spaces: Hit<SearchResult>[];
            tracks: Hit<SearchResult>[];
          },
        );

        setSearchResults(resultsByEntityType);
      } catch (error) {
        if (error instanceof Error) {
          if (error.message.includes("403")) {
            setSearchResults(null);
          }
        }

        console.error("Search error:", error);
        const errorId = uuidv4();
        addMessage({
          id: errorId,
          children: (
            <ToastMessageContent
              id={errorId}
              title="Search error"
              text={
                error instanceof Error
                  ? error.message
                  : "Error searching. Please try again."
              }
              type="error"
            />
          ),
          type: "error",
          expireAt: Date.now() + 3000, // 3 seconds from now
        });
      } finally {
        setIsSearching(false);
      }
    }, 1000),
    [client],
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchValue(newValue);

    if (newValue) {
      debouncedSearch(newValue);
    } else {
      // Cancel any pending debounced searches when the input is cleared.
      // This prevents stale search results from appearing after the input is emptied.
      // The cancel method is attached to the debounced function by lodash and maintains
      // the same reference through useCallback, allowing it to cancel the correct pending search.
      debouncedSearch.cancel();
      setSearchResults(null);
    }
  };

  const isSearchClientLoaded = !!client;

  const value = {
    searchValue,
    isSearchClientLoading,
    searchError,
    searchResults,
    isSearchClientLoaded,
    isSearching,
    handleChange,
  };

  return (
    <SearchContext.Provider value={value}>{children}</SearchContext.Provider>
  );
};

export const useSearchContext = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error(
      "useSearchContext must be used within a SearchContextProvider",
    );
  }
  return context;
};
